<template>
   <div>
      <div v-if="!loaded">
         <SpinnerOverlay />
      </div>
      <b-container class="client-dashboard">
         <PageTitle title="Welcome back!" />
         <b-row class="mb-4">
            <b-col xl="6" cols="12" class="progress-card-wrapper">
               <b-card id="progress-card" class="info-card h-md-100 mb-4 mb-md-0">
                  <div class="d-flex flex-column continue-section-wrapper">
                     <h3 class="mb-3 card-title bold">Your Progress</h3>
                     <span id="progress-percentage" class="progress-percentage">
                        {{ totalProgress.total }}% Complete
                        {{ openStudies.length > 1 ? `for ${openStudies.length} open studies` : '' }}
                     </span>
                     <b-progress
                        :value="totalProgress.total"
                        :max="100"
                        variant="white"
                        class="progress-white mb-2"
                     ></b-progress>

                     <div v-if="openStudies.length > 1" class="study-prog-container">
                        <div
                           v-for="(study, idx) in openStudies"
                           :key="`study-prog-${study.id}`"
                           class="study-prog"
                        >
                           <span
                              :id="`progress-percentage-${idx + 1}`"
                              class="mr-2 progress-percentage-study"
                           >
                              {{ study.label }} - {{ studyProgress(study.id).total }}%
                           </span>
                           <b-progress
                              :value="studyProgress(study.id).total"
                              :max="100"
                              variant="white"
                              class="progress-white mb-2"
                           ></b-progress>
                        </div>
                     </div>
                  </div>
               </b-card>
            </b-col>
            <b-col xl="6" cols="12" class="progress-card-wrapper">
               <b-card class="info-card h-md-100 mb-4 mb-md-0">
                  <div class="d-flex flex-column continue-section-wrapper">
                     <h3 class="mb-3 card-title bold">
                        {{ continueCardTitle }}
                     </h3>
                     <div class="d-flex justify-content-between align-items-center column-gap">
                        <p v-if="nextAction" class="continue-section-subtitle">
                           {{ nextAction.text }}
                        </p>
                        <p v-else-if="totalProgress < 100" class="continue-section-subtitle">
                           Thank you, all your work is complete. We will notify you when your
                           attention is needed again.
                        </p>
                        <p v-else class="continue-section-subtitle">
                           Thank you. We will review your information and contact you when your
                           study is ready, or if there are any questions.
                        </p>
                        <div style="display: flex; flex-direction: row; gap: 5px">
                           <b-button
                              id="btn-continue"
                              class="continue-button"
                              v-if="nextAction"
                              variant="success"
                              @click="navigateToAction(nextAction.to, nextAction.studyId)"
                           >
                              Continue
                           </b-button>
                        </div>
                     </div>
                  </div>
               </b-card>
            </b-col>
         </b-row>
         <b-row class="my-4">
            <b-col cols="12">
               <b-card id="attention-card" class="info-card" style="background-color: white">
                  <h3
                     class="mb-3 card-title bold"
                     :class="{
                        'text-danger': attentionItems.length > 0,
                        'no-action-items': attentionItems.length === 0,
                     }"
                  >
                     {{
                        attentionItems.length > 0
                           ? 'For your attention'
                           : 'There are no items that require your attention.'
                     }}
                  </h3>
                  <div
                     class="
                        d-flex
                        flex-column
                        align-items-start
                        justify-content-between
                        attention-list-wrapper
                     "
                  >
                     <div
                        v-for="item in attentionItems"
                        class="d-flex align-items-center justify-content-between attention-list-row"
                        :key="`${item.text}-${item.studyId}`"
                     >
                        <div class="d-flex align-items-center attention-list-icon-with-text">
                           <custom-icon
                              icon="icon-alert"
                              style="font-size: 1rem"
                              width="1.0625rem"
                              height="1.25rem"
                           />
                           <span class="attention-text">{{ item.text }}</span>
                        </div>
                        <b-button
                           :id="`btn-attention-${item.to.name}`"
                           variant="success"
                           class="no-wrap d-flex align-items-center"
                           size="sm"
                           @click="navigateToAction(item.to, item.studyId)"
                           style="font-size: 0.75rem"
                        >
                           {{ item.buttonText }}
                           <custom-icon
                              icon="icon-chevron-right"
                              height="16px"
                              width="16px"
                              class="ml-1"
                           />
                        </b-button>
                     </div>
                  </div>
               </b-card>
            </b-col>
         </b-row>

         <h2 class="section-title">Progress Overview</h2>

         <div class="d-flex flex-column progress-section-wrapper">
            <!-- EMPLOYEE TIME CARD -->
            <CustomerDashboardCard
               ident="time-survey"
               title="Employee Time"
               icon-name="icon-chronometer"
               :progress="progress.timeSurvey"
               :action-items="activeStudyItems(timeSurveyActionItems)"
               :completed-action-items="activeStudyItems(timeSurveyCompletedItems)"
            ></CustomerDashboardCard>

            <!-- CONTRACTOR CARD -->
            <CustomerDashboardCard
               ident="contractor"
               title="Contract Research"
               icon-name="icon-contractor"
               :progress="progress.contractors"
               :action-items="activeStudyItems(contractorActionItems)"
               :completed-action-items="activeStudyItems(contractorCompletedItems)"
            ></CustomerDashboardCard>

            <!-- SUPPLIES CARD -->
            <CustomerDashboardCard
               ident="supplies"
               title="Supplies"
               icon-name="icon-supplies"
               :progress="progress.supplies"
               :action-items="activeStudyItems(suppliesActionItems)"
               :completed-action-items="activeStudyItems(suppliesCompletedItems)"
            ></CustomerDashboardCard>

            <!-- CLOUD COMPUTING CARD -->
            <CustomerDashboardCard
               ident="cloud"
               title="Cloud Computing"
               icon-name="icon-cloud-computing"
               :progress="progress.cloud"
               :action-items="activeStudyItems(cloudActionItems)"
               :completed-action-items="activeStudyItems(cloudCompletedItems)"
            ></CustomerDashboardCard>

            <!-- COMPANY CARD -->
            <CustomerDashboardCard
               v-if="companyQuestionnaires.length > 0"
               ident="company"
               title="Company Questionnaires"
               icon-name="icon-office-building"
               :progress="progress.company"
               :action-items="activeStudyItems(companyActionItems)"
               :completed-action-items="activeStudyItems(companyCompletedItems)"
            ></CustomerDashboardCard>

            <!-- PROJECT CARD -->
            <CustomerDashboardCard
               v-if="projectQuestionnaires.length > 0"
               ident="project"
               title="Project Questionnaires"
               icon-name="icon-workflow"
               :progress="progress.project"
               :action-items="activeStudyItems(projectActionItems)"
               :completed-action-items="activeStudyItems(projectCompletedItems)"
            ></CustomerDashboardCard>

            <!-- PROFILE CARD -->
            <CustomerDashboardCard
               ident="profile"
               title="Profiles"
               icon-name="icon-avatar"
               :progress="progress.profile"
            >
               <div
                  v-for="(item, idx) in profileActionItems"
                  :key="`${item.data.userId}:${item.studyId}`"
                  class="d-flex align-items-center justify-content-between pt-3"
               >
                  <span :id="`profile-name-${idx + 1}`" class="table-item">
                     {{ `${item.data.userFirstName} ${item.data.userLastName}` }}
                     {{ openStudies.length > 1 ? `(${studyMap[item.studyId].label})` : '' }}
                  </span>
                  <div class="d-flex align-items-center">
                     <span :id="`profile-percentage-${idx + 1}`" class="mr-2 table-item"
                        >{{ profilePercentage(item) }}% Complete</span
                     >
                     <b-progress
                        :value="profilePercentage(item)"
                        :max="100"
                        style="min-width: 14rem"
                        :variant="
                           item.action === 'PROFILE_QUESTIONNAIRE_FINISHED' ? 'success' : 'primary'
                        "
                     >
                     </b-progress>
                  </div>
               </div>
            </CustomerDashboardCard>

            <!-- STUDY SETUP CARD -->
            <CustomerDashboardCard
               ident="studySetup"
               title="Study Setup"
               icon-name="icon-workflow"
               :progress="progress.taxReturns"
               :action-items="activeStudyItems(studySetupActionItems)"
               :completed-action-items="activeStudyItems(studySetupCompletedItems)"
            ></CustomerDashboardCard>
         </div>
      </b-container>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {SidebarStates} from '@/store/modules/session';

import CustomerDashboardCard from './widgets/CustomerDashboardCard';
import {sortBy} from '../../helpers/utils';
import SpinnerOverlay from '@/views/common/widgets/SpinnerOverlay';

export default {
   components: {
      CustomerDashboardCard,
      SpinnerOverlay,
   },

   data() {
      return {
         SidebarStates,
         loaded: false,
      };
   },

   computed: {
      ...mapGetters({
         profile: 'profile',
         greeted: 'greeted',
         manager: 'manager',
         openStudies: 'companies/openStudies',
         msgCollapsed: 'messaging/clientMessagingCollapsed',
         studyMap: 'companies/studyMap',
         progress: 'progress/activeStudyProgress',
         totalProgress: 'progress/totalProgress',
         studyProgress: 'progress/studyProgress',
         attentionCardActionItems: 'progress/attentionCardActionItems',
         timeSurveyActionItems: 'progress/timeSurveyActionItems',
         timeSurveyCompletedItems: 'progress/timeSurveyCompletedItems',
         projectActionItems: 'progress/projectActionItems',
         projectCompletedItems: 'progress/projectCompletedItems',
         companyActionItems: 'progress/companyActionItems',
         companyCompletedItems: 'progress/companyCompletedItems',
         studySetupActionItems: 'progress/studySetupActionItems',
         studySetupCompletedItems: 'progress/studySetupCompletedItems',
         company: 'companies/currentCompany',
         activeStudy: 'companies/activeStudy',
         suppliesActionItems: 'progress/suppliesActionItems',
         suppliesCompletedItems: 'progress/suppliesCompletedItems',
         contractorActionItems: 'progress/contractorActionItems',
         contractorCompletedItems: 'progress/contractorCompletedItems',
         cloudActionItems: 'progress/cloudActionItems',
         cloudCompletedItems: 'progress/cloudCompletedItems',
         cloudAssignee: 'cloud/assignee',
         _profileActionItems: 'progress/profileActionItems',
         _suppliesAssignee: 'supplies/assignee',
         _users: 'users/companyUsers',
         isActiveStudyInSetupStage: 'companies/isActiveStudyInSetupStage',
         activeStudyId: 'companies/activeStudyId',
         companyQuestionnaires: 'questionnaire/companyQuestionnaires',
         projectQuestionnaires: 'questionnaire/projectQuestionnaires',
      }),

      actionFields() {
         const actionFields = [
            {key: 'text', label: 'Incomplete', sortable: true},
            {key: 'actions', label: ''},
         ];

         if (this.openStudies.length > 1) {
            actionFields.unshift({
               key: 'studyId',
               label: 'Study',
               formatter: (value) => this.studyMap[value]?.label,
               sortable: true,
               sortByFormatted: true,
            });
         }

         return actionFields;
      },

      continueCardTitle() {
         if (this.totalProgress === 0) {
            return "Let's get started!";
         } else if (this.nextAction) {
            return 'Continue where you left off';
         } else {
            return 'All done!';
         }
      },

      companyId() {
         return this.profile.companyId;
      },

      nextAction() {
         if (this.attentionItems.length === 0) {
            return null;
         } else {
            return this.attentionItems[0];
         }
      },

      attentionItems() {
         return this.attentionCardActionItems.filter((item) => item.studyId == this.activeStudyId);
      },

      profileActionItems() {
         const profileActionItems = this.activeStudyItems(this._profileActionItems);

         profileActionItems.sort(
            sortBy((i) => `${i.data.userFirstName} ${i.data.userLastName}`.toLocaleLowerCase())
         );

         return profileActionItems;
      },

      suppliesAssigneeName() {
         const assigneeId = this._suppliesAssignee;

         if (assigneeId === null) {
            return null;
         }

         const assigneeUser = this._users[assigneeId];
         return assigneeUser ? `${assigneeUser.firstName} ${assigneeUser.lastName}` : '';
      },

      cloudAssigneeName() {
         if (this.cloudAssignee === null) {
            return null;
         }

         const assignee = this._users[this.cloudAssignee];
         return assignee ? `${assignee.firstName} ${assignee.lastName}` : '';
      },
   },

   methods: {
      /** Percentage complete for an individual profile questionnaire */
      profilePercentage(item) {
         const total = item.data.count;
         let complete = item.data.count - item.data.unanswered.length;
         return Math.floor((complete / total) * 100);
      },

      toAction(route, studyId) {
         return {
            name: route.name,
            params: {
               ...(route.params || {}),
               activeStudyId: studyId,
            },
            query: route.query,
            hash: route.hash,
         };
      },

      navigateToAction(to, studyId) {
         if (to.params?.shouldOpenTaxReturnModal) {
            this.$store.commit('setTaxReturnModalOpened', true);
         }
         this.$router.push(this.toAction(to, studyId));
      },

      activeStudyItems(items) {
         return items.filter((item) => item.studyId == this.activeStudyId);
      },

      async loadData() {
         this.loaded = false;
         let requests = [
            this.$store.dispatch('progress/loadProgress', {
               companyId: this.companyId,
               allStudies: true,
            }),
            this.$store.dispatch('users/loadCompanyUsers', {companyId: this.companyId}),
         ];

         if (this.company === null) {
            requests.push(
               this.$store.dispatch('companies/loadCompany', {companyId: this.companyId})
            );
         }

         await this.loadPage(requests);
         this.loaded = true;
      },
   },

   async created() {
      await this.loadData();
   },

   watch: {
      async activeStudyId() {
         if (this.isActiveStudyInSetupStage) {
            this.$router.push({name: 'study-setup'});
         } else {
            await this.loadData();
         }
      },
   },
};
</script>

<style lang="scss" scoped>
.client-dashboard {
   background: #f4f4f4;
   padding: 0 1rem 0 4rem;

   @media (max-width: 1240px) {
      margin-right: 3.125rem;
   }

   @media (max-width: 992px) {
      max-width: 760px;
      margin-right: 1rem;
   }

   .progress-section-wrapper .collapse-card {
      border-radius: 0;
   }

   .progress-section-wrapper .collapse-card:first-child {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
   }

   .progress-section-wrapper .collapse-card:last-child {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
   }
}

.progress-card-wrapper {
   @media (max-width: 1240px) {
      padding-bottom: 1.25rem;
   }
}

.section-title {
   font-size: 1.25rem;
   font-weight: 700;
   margin-bottom: 1.375rem;
   margin-left: 0.625rem;
}

.card-body {
   padding: 0 !important;
   margin: 1.5rem;
   display: flex;
   flex-direction: column;
   justify-content: center;
}

.attention-list-wrapper {
   overflow: auto;
}

.attention-list-row {
   width: 100%;
   padding: 0.25rem 0;
   border-bottom: 1px solid #e7ebf1;
   min-height: 48px;
}

.attention-list-row:last-child {
   border-bottom: 0;
}

.attention-list-icon-with-text {
   flex: 0.97;
   gap: 0.25rem;
}

.configuration-section-wrapper {
   display: flex;
   flex-wrap: wrap;
   justify-content: space-between;
   align-items: center;
   gap: 1rem;
   margin-bottom: 3rem;
}

.configuration-card {
   flex-basis: calc(33% - 1rem);

   &--sidebar-open {
      flex-basis: calc(50% - 1rem);

      @media (max-width: 1150px) {
         flex-basis: 100% !important;
      }
   }

   &:hover {
      border-color: #8ebde8;
   }

   @media (max-width: 1320px) {
      flex-basis: calc(50% - 1rem);
   }
}

.info-card {
   background-color: $secondary;
   color: $white;
   border: 1px solid #e6f1fb;
   border-radius: 10px;
   max-height: 18.75rem;

   @include md {
      height: 100%;
   }
}

.attention-text {
   font-size: 0.8125rem;
   color: $black;
   max-height: 2.4735rem;
   overflow: hidden;
}

.continue-section-subtitle {
   font-size: 0.8125rem;
   flex: 1;
}

.continue-button {
   font-size: 0.75rem;
}

.continue-section-wrapper {
   gap: 0.75rem;
}

.continue-section-title {
   font-size: 0.875rem;
   font-weight: 600;
}

.progress-percentage-study {
   font-size: 0.8125rem;
}

.progress-percentage {
   font-size: 0.875rem;
}

.card-title {
   font-size: 1.125rem;
}

.exclamation-icon {
   width: 17px;
   height: 20px;
}

.study-prog-container {
   display: flex;
   flex-direction: column;
   align-items: flex-end;
   margin-top: 1rem;
   max-height: 110px;
   overflow-y: auto;

   .study-prog {
      display: flex;

      .progress {
         width: 12rem;
      }
   }
}

::v-deep .card-body {
   justify-content: flex-start;
}

.table-item {
   font-size: 0.8125rem;
}

.no-action-items {
   font-size: 1rem;
   margin-bottom: 0 !important;
   color: $gray-800;
}

.column-gap {
   column-gap: 1rem;
}
</style>
